import { useCallback, useRef } from 'react';

/**
 * Backdrop dismissal that survives text selection.
 *
 * The naive pattern — `onClick` on the overlay, `stopPropagation` on the panel — closes on a
 * click the user never intended. The browser dispatches `click` on the *closest common
 * ancestor* of the mousedown and mouseup targets. Drag a selection out of an input and release
 * over the backdrop, and that ancestor is the overlay itself: the `click` is born on the
 * overlay, so the panel's `stopPropagation` never runs and the modal closes — taking the whole
 * form with it. That gesture is exactly how you clear an autofilled field.
 *
 * The only reliable signal that the user meant to dismiss is that the *entire* gesture happened
 * on the overlay: mousedown, mouseup and click all with `target === currentTarget`.
 *
 * Pure and hook-only: testable with Jest without mounting a modal.
 */
export default function useOverlayDismiss(onDismiss) {
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const startedOnOverlay = useRef(false);
  const endedOnOverlay = useRef(false);

  const onMouseDown = useCallback((e) => {
    startedOnOverlay.current = e.target === e.currentTarget;
  }, []);

  const onMouseUp = useCallback((e) => {
    endedOnOverlay.current = e.target === e.currentTarget;
  }, []);

  const onClick = useCallback((e) => {
    const dismiss =
      startedOnOverlay.current && endedOnOverlay.current && e.target === e.currentTarget;
    // Reset before dispatching, so a thrown handler can't leave the refs armed for the next gesture.
    startedOnOverlay.current = false;
    endedOnOverlay.current = false;
    if (dismiss) onDismissRef.current?.();
  }, []);

  return { onMouseDown, onMouseUp, onClick };
}
